// FileRow — full-width file/artifact row (icon + label + trailing note),
// clickable or static. i18n-clean: `label`/`note` arrive via props; the
// primitive imports only from `./` (ui), never a feature.
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { Icon, type IconName } from './Icon.js';

export interface FileRowProps {
  icon: IconName;
  label: string;
  note?: string;
  onClick?: () => void;
}

const ROW_BASE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '6px 8px',
  borderRadius: 'var(--r-2)',
};

export function FileRow({ icon, label, note, onClick }: FileRowProps) {
  const [hover, setHover] = useState(false);
  const clickable = onClick !== undefined;

  const content = (
    <>
      <Icon name={icon} size={14} style={{ color: 'var(--fg-3)' }} />
      <span
        style={{
          // minWidth:0 lets this flex item shrink below its content width so
          // the ellipsis engages instead of the label expanding the row.
          minWidth: 0,
          fontSize: 13,
          color: 'var(--fg-1)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1 }} />
      {note !== undefined && (
        <span style={{ fontSize: 11, color: 'var(--fg-3)', flex: 'none', textAlign: 'right' }}>
          {note}
        </span>
      )}
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          ...ROW_BASE,
          background: hover ? 'var(--bg-2)' : 'transparent',
          border: 0,
          textAlign: 'left',
          cursor: 'pointer',
          font: 'inherit',
          color: 'inherit',
        }}
      >
        {content}
      </button>
    );
  }

  return <div style={ROW_BASE}>{content}</div>;
}
